import { put, select, takeEvery } from "redux-saga/effects";

import { BillingRunAction } from "actions";

import { BILLING_RUN } from "constants/actionTypes";

import { requiresUserRuleBookSelection } from "lib/billingRuns";
import { deserializeBillingRun } from "lib/serializers/billingRuns";

import {
  getActiveBillingRunId,
  getBillingRuns,
  getDefaultClearingSaleRuleBookId,
  getIsCurrentSaleClearingSale,
  getIsReadOnlyByBillingRunId,
} from "selectors";

// Activates the billing run only if it does not require user rule book selection.
// Ensures runs requiring rule book confirmation do not become active.
function* subscribeIfSelectable(billingRun) {
  const state = yield select();
  const isClearingSale = getIsCurrentSaleClearingSale(state);
  const defaultRuleBookId = getDefaultClearingSaleRuleBookId(state);
  if (!requiresUserRuleBookSelection(billingRun, isClearingSale, defaultRuleBookId)) {
    yield put(BillingRunAction.subscribe(billingRun.id));
  }
}

function* onEnableBillingRun(action) {
  const { livestockSaleIds = [] } = action;
  if (livestockSaleIds.length === 0) {
    return;
  }
  yield put(BillingRunAction.request({ livestockSalesIdIn: livestockSaleIds }));
}

function* onCreateBillingRunSuccess(action) {
  const billingRun = deserializeBillingRun(action.payload);
  yield subscribeIfSelectable(billingRun);
}

function* onSubscribeBillingRun(action) {
  const { id } = action;
  const state = yield select();
  const readOnly = getIsReadOnlyByBillingRunId(id)(state);
  if (readOnly === false) {
    yield put(BillingRunAction.checkForChanges(id));
  }
}

function* onUpdateBillingRunSuccess(action) {
  const billingRun = deserializeBillingRun(action.payload);
  const activeBillingRunId = yield select(getActiveBillingRunId);
  if (activeBillingRunId === billingRun.id) {
    return;
  }
  yield subscribeIfSelectable(billingRun);
}

function* onFetchSuccess(action) {
  // If there is only one BillingRun, make it active.
  if (!action.meta.cacheHit) {
    const state = yield select();
    const billingRuns = getBillingRuns(state);
    if (Object.keys(billingRuns).length === 1) {
      const onlyBillingRunId = Object.keys(billingRuns)[0];
      const activeBillingRunId = getActiveBillingRunId(state);
      const onlyBillingRun = billingRuns[onlyBillingRunId];
      if (activeBillingRunId !== onlyBillingRunId) {
        yield subscribeIfSelectable(onlyBillingRun);
      }
    }
  }
}

export default function* billingRunSagas() {
  yield takeEvery(BILLING_RUN.ENABLE.ACTION, onEnableBillingRun);
  yield takeEvery(BILLING_RUN.CREATE.SUCCESS, onCreateBillingRunSuccess);
  yield takeEvery(BILLING_RUN.UPDATE.SUCCESS, onUpdateBillingRunSuccess);
  yield takeEvery(BILLING_RUN.SUBSCRIBE.ACTION, onSubscribeBillingRun);
  yield takeEvery(BILLING_RUN.FETCH_BULK.SUCCESS, onFetchSuccess);
  yield takeEvery(BILLING_RUN.FETCH_CHANGES.SUCCESS, onFetchSuccess);
}
